import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Client } from '../models/client.model';
import { ErrorService } from './error.service';
import { MsAuthService } from './ms-auth.service';

export interface IClientSearchParams {
  email?: string;
  firstName?: string;
  lastName?: string;
  styleAdvisor?: string;
  transactionsSince?: string;
  query?: string;
  raw?: string;
  count?: number;
  fields?: string[];
  phoneNumber?: string;
  country?: string;
  ceId?: string;
  sfccId?: string;
  customerId?: string;
  page?: number;
}

export interface ICDCResponse {
  data: ICDCData;
  profile: ICDCProfile;
  emails?: {
      verified: {},
      unverified: {},
  }
  preferences?: {
      loyaltyProgramEnrollment?:{
          isConsentGranted?: boolean
      }
  }
  UID?: string;
}
export interface ICDCProfile {
  lastName?: string;
  country?: string;
  city?: string;
  timezone?: string;
  phones?: ICDCPhones;
  locale?: string;
  state?: string;
  coordinates?: {
      lng?: string;
      lat?: string;
  }
  email?: string;
  firstName?: string;
}

interface ICDCPhones {
  number: string;
}

export interface ICDCData {
  addresses?: ICDCAddress[];
  systemFlags: ICDCSystemFlags;
  phones: ICDCPhone[];
  unsavedAddresses: ICDCUnsavedAddress[];
  mappedIDs: ICDCMappedIDs;
  customer: ICDCCustomer;
}
interface ICDCCustomer {
  isLiteAccount?: boolean;
  isGuest?: boolean;
  createdStore?: string;
  discount?: any[];
  fullName?: string;
  styleAdvisor?: any[];
  consent?: ICDCConsent;
  hasEcommAccount?: boolean;
  profileType?: string;
  phone?: ICDCPhone2;
  location?: ICDCLocation;
  event?: ICDCEvent;
  email?: string;
}
interface ICDCEvent {
  snapshotDate: string;
  primaryRegion: string;
  primaryState: string;
  endDate: string;
  channel: string;
  styleAdvisor: string;
  type: string;
  primaryStore: string;
  totalSpend: string;
  tier: string;
  spendRange: string;
  name: string;
  ID: string;
  giftEligibility: boolean;
  primaryCountry: string;
  startDate: string;
  group: string;
}
interface ICDCLocation {
  country: string;
  city: string;
  state: string;
  coordinates: {
      lng: string;
      lat: string;
  }
}
interface ICDCPhone2 {
  number: string;
  countryCode: string;
}
interface ICDCConsent {
  lastTransactionDate: string;
  lastTransactionID: string;
  acquisitionSource: string;
  region: string;
  isEmailable: boolean;
  isCallable: boolean;
  isTextable: boolean;
  isStyleAdvisorEmailable: boolean;
  isStyleAdvisorCallable: boolean;
  isStyleAdvisorTextable: boolean;
}
interface ICDCMappedIDs {
  ce?: string[];
  cdc?: any[];
  sfcc?: string[];
  c4cInternalCustId?: string[];
  xstore?: string[];
  apropos?: any[];
  c4cUuid?: string[];
}
interface ICDCUnsavedAddress {
  country: string;
  firstName: string;
  lastName: string;
  city: string;
  address1: string;
  type: string;
}
interface ICDCPhone {
  number: string;
  type?: string;
  preferred: boolean;
}
interface ICDCSystemFlags {
  lastUpdatedBy: string;
  isNewReg: boolean;
  isCeImport?: boolean;
}
interface ICDCAddress {
  zip: string;
  country: string;
  firstName?: string;
  lastName?: string;
  city: string;
  phone?: string;
  address1: string;
  name: string;
  state?: string;
  type: string;
  preferred: boolean;
  address2?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CDCService {
  public clients: Client[] = [];
  private _host = `${environment.apigee.host}`;

  private _clients: BehaviorSubject<Client[] | null> = new BehaviorSubject<
    Client[] | null
  >(null);
  public clients$: Observable<Client[] | null> = this._clients.asObservable();

  constructor(
    private _httpClient: HttpClient,
    private _errorService: ErrorService,
    private _msAuthService: MsAuthService
  ) {}

  public clientSearch(params: IClientSearchParams): Observable<Client[]> {
    const url = `${this._host}/customers/cdc/raw`;

    let query = '';
    const fields = params.fields ? params.fields.join(',') : '*';

    params.page = params.page ? params.page : 1;
    params.count = params.count ? params.count : 20;
    const offset = params.count * (params.page - 1);

    if (params.raw) {
      query = params.raw;
    } else if (params.query) {
      query = `SELECT ${fields} FROM accounts WHERE \
      (data.customer.fullName = regex('.*${params.query}.*') \
      OR data.customer.location.country = regex('${params.query}.*') \
      OR data.customer.location.state = regex('${params.query}.*') \
      OR data.customer.location.city = regex('${params.query}.*') \
      OR (data.customer.styleAdvisor.assignedStyleAdvisor = regex('${params.query}.*') \
      OR data.customer.styleAdvisor.assignedStyleAdvisor CONTAINS regex('${params.query}.*')) \
      OR data.customer.email = regex('.*${params.query}.*')) AND \
      (data.customer.location.country is null OR data.customer.location.country in ('CA','US',' ')) \
      OR data.customer.profileType != 'EMPLOYEE' ORDER BY data.customer.fullName LIMIT ${params.count} START ${offset}`;
    } else {
      const conditions = <string[]>[];

      if (params.email) {
        conditions.push(`(profile.email = "${params.email}") OR (profile.email CONTAINS '${params.email}')
                        OR (loginIDs.emails = "${params.email}") OR (loginIDs.emails CONTAINS '${params.email}')
                        OR (loginIDs.unverifiedEmails = "${params.email}") OR (loginIDs.unverifiedEmails CONTAINS '${params.email}')
                        OR (emails.unverified = "${params.email}") OR (emails.unverified CONTAINS '${params.email}')`);
      }

      if (params.firstName) {
        conditions.push(`firstName like '${params.firstName}' OR firstName = '${params.firstName}'`);
      }

      if (params.lastName) {
        conditions.push(`lastName like '${params.lastName}' OR lastName = '${params.lastName}'`);
      }

      if (params.phoneNumber) {
        const formattedPhone = params.phoneNumber
          .replace(/\D/g, '')
          .split('')
          .join('%');
        conditions.push(
          `data.phones.number like '%${formattedPhone}' OR data.phones.number = '%${formattedPhone}' OR data.customer.phone.number like '%${formattedPhone}' OR data.customer.phone.number = '%${formattedPhone}'`
        );
      }

      if (params.styleAdvisor) {
        conditions.push(`(data.customer.styleAdvisor.assignedStyleAdvisor="${params.styleAdvisor}" \
        OR data.customer.styleAdvisor.assignedStyleAdvisor CONTAINS "${params.styleAdvisor}")`);
      }

      if (params.transactionsSince) {
        conditions.push(
          `data.customer.consent.lastTransactionDate >= "${params.transactionsSince}"`
        );
      }

      if (params.country) {
        conditions.push(`data.customer.location.country='${params.country}'`);
      }

      if (params.ceId) {
        conditions.push(`data.mappedIDs.ce CONTAINS '${params.ceId}'`);
      }

      if (params.sfccId) {
        conditions.push(`data.mappedIDs.sfcc CONTAINS '${params.sfccId}'`);
      }

      if (params.customerId) {
        conditions.push(
          `data.mappedIDs.ce='${params.customerId}' OR data.mappedIDs.sfcc='${params.customerId}'`
        );
      }

      query = `SELECT ${fields} FROM accounts WHERE (${conditions.join(
        ' AND '
      )}) ORDER BY data.customer.fullName LIMIT ${
        params.count || 20
      } START ${offset}`;
    }

    let body = JSON.stringify({
      raw: "query="+query
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type':  'application/json'
      })
    };

    return this._httpClient
      .post<ICDCResponse[]>(`${url}`, body, httpOptions)
      .pipe(
        map((result: ICDCResponse[]) => {
          this.clients = result.map((profile) =>
              this._msAuthService.isLoggedIn()
                  ? new Client(profile.data, profile.profile)
                  : new Client(profile.data)
          );
          this._clients.next(this.clients);
          return this.clients;
        }),
        catchError((e) => {
          this._errorService.showGeneralError(true);
          throw e;
        })
      );
  }

}
